import React, { useState, useEffect, useMemo, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from "react-router-dom";
import DataView, { parseNameTime } from "./components/DataView";
import ScrapeStarter from "./components/ScrapeStarter";
import About from "./components/About";
import ThemeToggle from "./components/ThemeToggle";
import NsfwToggle from "./components/NsfwToggle";
import ProfileModal from "./components/ProfileModal";
import AccountPanel from "./components/AccountPanel";
import VerifyPage from "./components/VerifyPage";
import NotificationsPage from "./components/NotificationsPage";
import AdminSubscribers from "./components/AdminSubscribers";
import NotifyIntroModal from "./components/NotifyIntroModal";
import { fetchScrapedData, fetchScrapeStatus, fetchVisitorCount, registerVisit } from "./api";
import { initGA, trackPageView, trackCoffeeClick, testAnalytics } from "./utils/analytics";
import { loadSubscriptions } from "./utils/subscriptionsStore";
import "./styles/enhanced-theme.css";

// Today's roster entries for a venue, in the same order shown in the table - used to
// build the profile modal's prev/next carousel order.
function getVenueEntries(data, venue) {
  if (!data) return [];
  switch (venue) {
    case "cleveland":
      return data.ginza?.rosters?.[0]?.names || [];
    case "elizabeth":
      return data.ginza479?.rosters?.[0]?.names || [];
    case "gladesville":
      return data.nightshade?.today || [];
    case "marrickville":
    default:
      return data.no5?.today || [];
  }
}

// SEO Content Component - Collapsible
function SEOContent() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className="seo-content mt-4 mb-4">
      <div className="card shadow-sm">
        <div 
          className="card-header bg-light cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="h6 mb-0 text-primary">
              📖 About Sydney Roster Scheduler
            </h2>
            <span className="badge bg-primary">
              {isExpanded ? '▲ Hide' : '▼ Learn More'}
            </span>
          </div>
        </div>
        
        {isExpanded && (
          <div className="card-body">
            <p className="card-text">
              Welcome to Sydney's most comprehensive roster tracking platform. Check daily work schedules 
              for multiple Sydney venues in one convenient location.
            </p>
            
            <p className="mb-3">
              <strong>Venues:</strong> No 5 Marrickville • Ginza Cleveland St • 479 Ginza Elizabeth St • The Nightshade Gladesville
            </p>
            
            <div className="d-flex gap-2">
              <Link to="/about" className="btn btn-primary btn-sm">
                Read Full About Page →
              </Link>
            </div>
            
            <hr className="my-3" />
            
            <div className="text-muted small">
              <p className="mb-0">
                <strong>Keywords:</strong> Sydney roster, No 5 Marrickville roster, Cleveland Street schedule, 
                Elizabeth Street roster, Gladesville schedule, aus99, aus99 forum, venue rosters Sydney
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// Enhanced Buy Me a Coffee Button Component
function BuyMeACoffeeButton() {
  const handleClick = () => {
    trackCoffeeClick();
  };

  return (
    <a
      href="https://www.buymeacoffee.com/sydneyscheduler"
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className="coffee-button"
    >
      <span>☕</span>
      Buy me a coffee
    </a>
  );
}

// Component to track page views
function Analytics() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  return null;
}

// Small ticking Sydney clock - reinforces that this is a live board, not a static page.
function SydneyClock() {
  const [time, setTime] = useState("");

  useEffect(() => {
    const formatter = new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Sydney",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    const tick = () => setTime(formatter.format(new Date()));
    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <span className="sydney-clock" aria-label="Current time in Sydney">
      <span className="clock-label">SYD</span>
      <span className="clock-time">{time}</span>
    </span>
  );
}

// Component to dynamically update canonical tag based on route
function CanonicalTag() {
  const location = useLocation();

  useEffect(() => {
    // Remove any existing canonical tags
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }

    // Create new canonical tag with current URL
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = `https://sydneyscheduler.com${location.pathname}`;
    document.head.appendChild(canonical);

    // Update meta description and title based on route
    const metaDescription = document.querySelector('meta[name="description"]');
    const pageTitle = document.querySelector('title');
    
    if (location.pathname === '/about') {
      if (metaDescription) {
        metaDescription.setAttribute('content', 
          'Learn about Sydney Roster Scheduler - Your trusted source for No 5 Marrickville roster, Ginza Cleveland St schedule, 479 Ginza Elizabeth St roster, and The Nightshade Gladesville roster. Updated daily with accurate venue information.'
        );
      }
      if (pageTitle) {
        pageTitle.textContent = 'About Us | Sydney Roster Scheduler - No 5 Marrickville, Ginza, Nightshade';
      }
    } else if (location.pathname === '/') {
      if (metaDescription) {
        metaDescription.setAttribute('content', 
          'Live daily roster schedules for No 5 Marrickville, Ginza Cleveland St, 479 Ginza Elizabeth St, and The Nightshade Gladesville. Updated daily with current and next day rosters.'
        );
      }
      if (pageTitle) {
        pageTitle.textContent = 'Sydney Roster Scheduler | No 5 Marrickville, Cleveland, Elizabeth St, Gladesville';
      }
    }

    // Cleanup function
    return () => {
      const canonicalToRemove = document.querySelector('link[rel="canonical"]');
      if (canonicalToRemove) {
        canonicalToRemove.remove();
      }
    };
  }, [location]);

  return null;
}

function App() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastDuration, setLastDuration] = useState("");
  const [visitorCount, setVisitorCount] = useState(null);
  const [showAccount, setShowAccount] = useState(false);
  const scrapeStateRef = useRef({ status: null, completedAt: null });

  const [hash, setHash] = useState(window.location.hash);

  let profileVenue = "marrickville";
  let profileName = "";
  
  if (hash.startsWith('#profile-')) {
    const hashData = decodeURIComponent(hash.replace('#profile-', ''));
    if (hashData.startsWith('gladesville-')) {
      profileVenue = "gladesville";
      profileName = hashData.replace('gladesville-', '');
    } else if (hashData.startsWith('cleveland-')) {
      profileVenue = "cleveland";
      profileName = hashData.replace('cleveland-', '');
    } else if (hashData.startsWith('elizabeth-')) {
      profileVenue = "elizabeth";
      profileName = hashData.replace('elizabeth-', '');
    } else if (hashData.startsWith('marrickville-')) {
      profileVenue = "marrickville";
      profileName = hashData.replace('marrickville-', '');
    } else {
      profileName = hashData; // Fallback
    }
    profileName = profileName.replace(/[\s-]+$/, '');
  }

  // Ordered names for the venue currently shown in the profile modal, used to
  // navigate to the previous/next profile with the arrow buttons or keys.
  const venueNames = useMemo(() => {
    return getVenueEntries(data, profileVenue)
      .map((entry) => parseNameTime(entry).name.replace(/[\s-]+$/, ''))
      .filter(Boolean);
  }, [data, profileVenue]);

  const navigateProfile = (direction) => {
    if (venueNames.length < 2) return;
    const currentIndex = venueNames.indexOf(profileName);
    if (currentIndex === -1) return;
    const nextIndex = (currentIndex + direction + venueNames.length) % venueNames.length;
    window.location.hash = `#profile-${profileVenue}-${venueNames[nextIndex]}`;
  };

  // Count this browser as a visitor once, then always show the current total.
  useEffect(() => {
    const alreadyCounted = localStorage.getItem('visitor_counted');
    const request = alreadyCounted ? fetchVisitorCount() : registerVisit();
    if (!alreadyCounted) {
      localStorage.setItem('visitor_counted', 'true');
    }
    request.then((result) => setVisitorCount(result.count)).catch(() => {});
  }, []);

  // Load the signed-in user's notification subscriptions (if any) and let a signed-out
  // NotifyButton click open this panel instead of silently failing.
  useEffect(() => {
    loadSubscriptions();
    const openPanel = () => setShowAccount(true);
    window.addEventListener("open-account-panel", openPanel);
    return () => window.removeEventListener("open-account-panel", openPanel);
  }, []);

  // Initialize Google Analytics on app load
  useEffect(() => {
    initGA();
    
    // Add test function to window for easy debugging
    window.testAnalytics = testAnalytics;
    console.log('Analytics test function available: window.testAnalytics()');

    // Subscribe to hash changes to trigger modal updates immediately
    const handleHashChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    const fetchData = async ({ showLoading = false } = {}) => {
      if (showLoading) setLoading(true);
      setError(null);

      try {
        const result = await fetchScrapedData();
        setData(result);

        const scrapeStatus = await fetchScrapeStatus();
        setLastDuration(scrapeStatus?.duration_human || "");
        scrapeStateRef.current = {
          status: scrapeStatus?.status || null,
          completedAt: scrapeStatus?.completed_at || null,
        };
      } catch (err) {
        setError(err.message || "Unknown error");
      } finally {
        if (showLoading) setLoading(false);
      }
    };

    const checkForFreshResults = async () => {
      try {
        const scrapeStatus = await fetchScrapeStatus();
        const prev = scrapeStateRef.current;
        const nextStatus = scrapeStatus?.status || null;
        const nextCompletedAt = scrapeStatus?.completed_at || null;
        const justCompleted = prev.status === "in_progress" && nextStatus === "completed";
        const newCompletedRun = nextStatus === "completed" && nextCompletedAt && nextCompletedAt !== prev.completedAt;

        setLastDuration(scrapeStatus?.duration_human || "");

        scrapeStateRef.current = {
          status: nextStatus,
          completedAt: nextCompletedAt,
        };

        if (justCompleted || newCompletedRun) {
          const result = await fetchScrapedData();
          setData(result);
        }
      } catch (_err) {
        // Silent background polling failure; regular user actions can still refresh data.
      }
    };

    fetchData({ showLoading: true });

    const intervalId = setInterval(checkForFreshResults, 15000);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForFreshResults();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return (
    <Router>
      <Analytics />
      <CanonicalTag />
      <div className="container mt-4">
        {/* Header with title, theme toggle, and coffee button */}
        <header className="marquee-header d-flex justify-content-between align-items-center mb-4 border-bottom">
          <div>
            <p className="marquee-eyebrow">
              <span className="bulb" aria-hidden="true" />
              Live roster board &middot; Sydney
            </p>
            <Link to="/" style={{ textDecoration: "none" }}>
              <h1 className="marquee-title">Sydney Roster Scheduler</h1>
            </Link>
            <p className="marquee-tagline mt-2 mb-0">Four venues, one board, updated around the clock.</p>
          </div>
          <div className="header-controls">
            <SydneyClock />
            <Link to="/notifications" className="btn btn-sm btn-outline-primary">
              🔔 Notifications
            </Link>
            <NsfwToggle />
            <ThemeToggle />
            <BuyMeACoffeeButton />
          </div>
        </header>

        {/* SEO Content Section - Collapsible at bottom */}
        <Routes>
          <Route path="/" element={
            <>
              <DataView data={data} loading={loading} error={error} />
              <SEOContent />
              {/* Home page only - an announcement shouldn't interrupt sign-in,
                  /verify, or the admin pages. */}
              <NotifyIntroModal />
            </>
          } />
          <Route path="/about" element={<About />} />
          <Route path="/start" element={<ScrapeStarter visitorCount={visitorCount} />} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/notifications" element={<NotificationsPage rosterData={data} />} />
          <Route path="/admin/subscribers" element={<AdminSubscribers />} />
        </Routes>

        <ProfileModal 
          show={hash.startsWith('#profile-')} 
          profileName={profileName}
          venue={profileVenue}
          rosterData={data}
          onNavigate={navigateProfile}
          onHide={() => { 
            window.history.replaceState(null, null, ' '); 
            setHash('');
          }}
        />

        <AccountPanel
          show={showAccount}
          onHide={() => setShowAccount(false)}
          rosterData={data}
        />
        
        {/* Footer */}
        <footer className="text-center mt-5 pt-4 border-top">
          <small className="text-muted">
            Last updated: {data?.no5?.timestamp ? new Date(data.no5.timestamp).toLocaleString() : 'Never'}
            {lastDuration ? ` (Last scrape took ${lastDuration})` : ""}
          </small>
        </footer>
      </div>
    </Router>
  );
}

export default App;